/**
 * The public frontend API another plugin builds widgets against.
 *
 * WHY THIS EXISTS. searchsync-components.js held a hardcoded list of imports and a matching list of
 * init calls — the frontend twin of the PHP-side ShortcodeType enum, and the last of the four closed
 * lists blocking the free/pro split. A widget shipped by a feature plugin had no way into that pass.
 * (The file even carried a TODO saying the list should be discovered rather than written out.)
 *
 * Registering from another bundle:
 *
 *     const ss = window.tangibleSearchSync;
 *     ss.registerWidgetInitializer( 'my-widget', () => {
 *       document.querySelectorAll( "[data-filter-type='myWidget']" ).forEach( container => {
 *         const search = ss.getInstance( container.getAttribute( 'data-index' ) );
 *         const data   = ss.getWidgetData( container.getAttribute( 'data-id' ) );
 *         …
 *       } );
 *     } );
 *
 * ORDERING IS THE WHOLE PROBLEM, and it is why registration exists at all rather than a feature
 * plugin simply adding its own DOMContentLoaded listener. createInstance's registerWidget() starts
 * every InstantSearch instance only once every [data-init] element on the page has reported in, and
 * once started, an instance will not accept widgets added afterwards. A late listener therefore
 * either misses the start or — worse, if its markup carries data-init — never reports in and hangs
 * the start for every widget on the page, core ones included.
 *
 * Registered initializers run inside core's own DOMContentLoaded pass, in the same synchronous block
 * as the core widgets, which puts them on the right side of that handshake. The requirement on the
 * other plugin is only that its script declares 'searchsync-components-js' as a dependency, so this
 * global exists by the time its module body runs.
 */

import { getInstance, registerWidget } from './searchsync-create-instance';
import { configure } from 'instantsearch.js/es/widgets';

/**
 * Keyed by name so a plugin re-registering (a script enqueued twice on one page, which WordPress
 * does not always prevent) replaces its initializer rather than running the widget twice — double
 * initialization here means duplicated InstantSearch widgets and doubled event handlers.
 */
const initializers = new Map();

/**
 * @param {string}   name Unique id for the widget, e.g. 'learndash/course-statuses'.
 * @param {Function} init Called once, on DOMContentLoaded, with no arguments.
 */
function registerWidgetInitializer( name, init ) {
  if ( typeof name !== 'string' || ! name ) return;
  if ( typeof init !== 'function' ) return;

  initializers.set( name, init );
}

/**
 * Run every registered initializer.
 *
 * Each is wrapped so one plugin's broken widget cannot stop the rest of the page initializing —
 * an uncaught throw here would abort the remainder of the DOMContentLoaded handler, taking core's
 * widgets down along with the offending one. The error is reported rather than swallowed, because
 * a widget that silently does nothing is the hardest kind of failure to diagnose.
 */
function runRegisteredWidgetInitializers() {
  initializers.forEach( ( init, name ) => {
    try {
      init();
    } catch ( error ) {
      console.error( `SearchSync: the "${ name }" widget failed to initialize.`, error );
    }
  } );
}

/**
 * The per-widget data PHP sent for `widgetId`, or {} when there is none.
 *
 * Every widget was decoding the base64 payload by hand, which meant each one also had to know that
 * PHP encodes with JSON_FORCE_OBJECT — the quirk that has already caused one blank-card outage by
 * turning a list into `{"0": …}`. One accessor means an outside widget inherits the handling
 * instead of rediscovering it.
 */
function getWidgetData( widgetId ) {
  if ( ! widgetId ) return {};

  try {
    const encoded = window.tgblSearchsyncDataEncoded;
    if ( ! encoded ) return {};

    const payload = JSON.parse( window.atob( encoded ) );

    return payload[ widgetId ] || {};
  } catch ( error ) {
    console.error( 'SearchSync: could not read the widget data payload.', error );
    return {};
  }
}

/**
 * Normalize a payload value that should be a list.
 *
 * PHP encodes the payload with JSON_FORCE_OBJECT, so a list arrives as `{"0": "a", "1": "b"}` and
 * every array method a widget would reach for is missing. Exposed because an outside widget hits
 * this the first time it reads a multi-value setting, and the failure looks like a data bug rather
 * than an encoding one.
 */
function toArray( value ) {
  if ( value == null ) return [];
  if ( Array.isArray( value ) ) return value;
  if ( typeof value === 'object' ) return Object.values( value );

  return [ value ];
}

const api = {
  registerWidgetInitializer,
  getInstance,
  registerWidget,
  getWidgetData,
  toArray,
  // Handed over rather than left for the other bundle to import, so a widget adds a Configure
  // built by the SAME copy of instantsearch.js that owns the instance it is added to. A second
  // bundled copy works right up until the two versions drift.
  widgets: { configure },
};

// Assigned at module load, not on DOMContentLoaded, so a dependent script's module body can call
// into it as soon as it runs.
window.tangibleSearchSync = Object.assign( window.tangibleSearchSync || {}, api );

export { registerWidgetInitializer, runRegisteredWidgetInitializers, getWidgetData, toArray };
